import React from 'react';
import { Scale, AlertTriangle, FileText, Users, Shield, Gavel } from 'lucide-react';

import { Banner } from './Banner';

export const TermsOfService = () => {
  return (
    <div className="page-content">
      {/* Shared Banner + Pill (reduced) with top spacing */}
      <Banner pillText="Terms of Service" reduced={true} withTopSpace={true} />
      {/* Hero Section */}
      <section className="pad-120 bg-gradient-to-br from-black to-gray-900">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <Scale style={{ color: 'var(--brand-primary)' }} size={64} className="mx-auto mb-6" />
            <h1 className="heading-1 mb-6">
              Terms of Service
            </h1>
            <p className="body-large mb-8">
              Terms and conditions governing your use of our website and services.
            </p>
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <p className="body-medium text-gray-300">
                <strong>Effective Date:</strong> January 1, 2024 | <strong>Last Updated:</strong> January 1, 2024
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pad-120">
        <div className="container max-w-4xl mx-auto">
          
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Agreement to Terms
            </h2>
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-8">
              <p className="body-medium text-gray-300 mb-4">
                Welcome to <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy. These Terms of Service ("Terms") govern 
                your use of our website located at www.gymratrehab.com (the "Service") operated by 
                <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy ("us," "we," or "our").
              </p>
              <p className="body-medium text-gray-300 mb-4">
                By accessing or using our Service, you agree to be bound by these Terms. If you 
                disagree with any part of these terms, then you may not access the Service.
              </p>
              <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 mt-6">
                <div className="flex items-start">
                  <AlertTriangle style={{ color: '#f59e0b' }} size={20} className="mr-3 mt-1 flex-shrink-0" />
                  <p className="body-small text-amber-200">
                    <strong>Important:</strong> These terms constitute a legally binding agreement. 
                    Please read them carefully before using our website or services.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Website Use */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Website Use
            </h2>
            
            <div className="space-y-8">
              <div className="use-section bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="flex items-start mb-4">
                  <Users style={{ color: 'var(--brand-primary)' }} size={32} className="mr-4 mt-1" />
                  <div>
                    <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Permitted Use</h3>
                    <p className="body-medium text-gray-300 mb-4">You may use our website for:</p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Learning about our physical therapy services</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Reading educational content and blog posts</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Contacting us for appointments or information</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Accessing pricing and service information</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="use-section bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="flex items-start mb-4">
                  <AlertTriangle style={{ color: '#ef4444' }} size={32} className="mr-4 mt-1" />
                  <div>
                    <h3 className="heading-4 mb-3" style={{ color: '#ef4444' }}>Prohibited Use</h3>
                    <p className="body-medium text-gray-300 mb-4">You may NOT use our website to:</p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0 bg-red-500"></div>
                        <span>Violate any applicable laws or regulations</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0 bg-red-500"></div>
                        <span>Transmit harmful or malicious code</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0 bg-red-500"></div>
                        <span>Attempt to gain unauthorized access to our systems</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0 bg-red-500"></div>
                        <span>Copy, reproduce, or distribute our content without permission</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0 bg-red-500"></div>
                        <span>Harass, abuse, or harm other users</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0 bg-red-500"></div>
                        <span>Use automated systems to scrape or harvest data</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Healthcare Disclaimer */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Healthcare Information Disclaimer
            </h2>
            
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Shield style={{ color: '#ef4444' }} size={32} className="mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="heading-4 mb-4" style={{ color: '#ef4444' }}>Important Medical Disclaimer</h3>
                  <div className="space-y-4 text-red-200">
                    <p className="body-medium">
                      <strong>The information on this website is for educational and informational purposes only 
                      and is not intended as medical advice.</strong>
                    </p>
                    <p className="body-medium">
                      The content should not be used to diagnose, treat, cure, or prevent any medical condition. 
                      Always seek the advice of your physician, physical therapist, or other qualified healthcare 
                      provider with any questions you may have regarding a medical condition.
                    </p>
                    <p className="body-medium">
                      <strong>Never disregard professional medical advice or delay in seeking it because of 
                      something you have read on this website.</strong>
                    </p>
                    <p className="body-medium">
                      If you think you may have a medical emergency, call your doctor or 911 immediately.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Intellectual Property Rights
            </h2>
            
            <div className="space-y-6">
              <div className="ip-section bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Our Content</h3>
                <p className="body-medium text-gray-300 mb-4">
                  The Service and its original content, including but not limited to text, graphics, 
                  logos, button icons, images, audio clips, digital downloads, and software, are the 
                  property of <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy and are protected by United States and 
                  international copyright laws.
                </p>
                <p className="body-medium text-gray-300">
                  The trademarks, service marks, and logos used and displayed on this Service are 
                  registered and unregistered trademarks of <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy and others.
                </p>
              </div>

              <div className="ip-section bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>User Content</h3>
                <p className="body-medium text-gray-300 mb-4">
                  By submitting content to our Service (such as comments, reviews, or contact form submissions), 
                  you grant us a non-exclusive, royalty-free, perpetual, and worldwide license to use, 
                  reproduce, modify, and display such content for business purposes.
                </p>
                <p className="body-medium text-gray-300">
                  You represent and warrant that you own or have the necessary rights to grant us 
                  this license and that your content does not violate any third-party rights.
                </p>
              </div>
            </div>
          </div>

          {/* Privacy and Data */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Privacy and Data Protection
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300 mb-4">
                Your privacy is important to us. Our collection and use of personal information is 
                governed by our <a href="/privacy-policy" className="text-lime-400 hover:underline">Privacy Policy</a>, 
                which is incorporated into these Terms by reference.
              </p>
              <p className="body-medium text-gray-300 mb-4">
                For healthcare-related information and patient privacy rights, please refer to our 
                <a href="/hipaa-notice" className="text-lime-400 hover:underline"> HIPAA Notice of Privacy Practices</a>.
              </p>
              <p className="body-medium text-gray-300">
                By using our Service, you consent to the collection, use, and disclosure of your 
                information as described in our Privacy Policy.
              </p>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Limitation of Liability
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300 mb-4">
                <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL 
                GYMRATREHAB PHYSICAL THERAPY BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, 
                SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES.</strong>
              </p>
              <p className="body-medium text-gray-300 mb-4">
                This includes, without limitation, damages for loss of profits, goodwill, use, data, 
                or other intangible losses arising out of or relating to the use of, or inability to 
                use, this Service.
              </p>
              <p className="body-medium text-gray-300">
                Our total liability to you for any damages shall not exceed the amount you have paid 
                us for services in the twelve (12) months prior to the event giving rise to the liability.
              </p>
            </div>
          </div>

          {/* Indemnification */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Indemnification
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300">
                You agree to defend, indemnify, and hold harmless <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy, 
                its officers, directors, employees, and agents, from and against any and all claims, 
                damages, obligations, losses, liabilities, costs, or debt, and expenses (including 
                attorney's fees) arising from your use of and access to the Service, your violation 
                of any term of these Terms, or your violation of any third-party right.
              </p>
            </div>
          </div>

          {/* Service Availability */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Service Availability
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="availability-item bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Website Availability</h3>
                <p className="body-medium text-gray-300">
                  We strive to maintain continuous operation of our website, but we cannot guarantee 
                  uninterrupted access. The Service may be temporarily unavailable due to maintenance, 
                  updates, or technical issues.
                </p>
              </div>

              <div className="availability-item bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Service Changes</h3>
                <p className="body-medium text-gray-300">
                  We reserve the right to modify, suspend, or discontinue any part of our Service 
                  at any time without prior notice. We may also impose limits on certain features 
                  or restrict access to parts of the Service.
                </p>
              </div>
            </div>
          </div>

          {/* Termination */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Termination
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300 mb-4">
                We may terminate or suspend your access to our Service immediately, without prior 
                notice or liability, for any reason whatsoever, including without limitation if you 
                breach the Terms.
              </p>
              <p className="body-medium text-gray-300 mb-4">
                Upon termination, your right to use the Service will cease immediately. All provisions 
                of the Terms which by their nature should survive termination shall survive termination.
              </p>
              <p className="body-medium text-gray-300">
                You may stop using our Service at any time by discontinuing your access to our website.
              </p>
            </div>
          </div>

          {/* Governing Law */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Governing Law and Jurisdiction
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300 mb-4">
                These Terms shall be interpreted and governed by the laws of the State of Arizona, 
                without regard to its conflict of law provisions.
              </p>
              <p className="body-medium text-gray-300 mb-4">
                Any legal action or proceeding arising under these Terms will be brought exclusively 
                in the federal or state courts located in Maricopa County, Arizona.
              </p>
              <p className="body-medium text-gray-300">
                You and <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy agree to submit to the personal jurisdiction 
                of such courts.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Contact Information
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300 mb-6">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Email</h3>
                  <p className="text-gray-300">Info@gymratrehab.com</p>
                </div>
                <div>
                  <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Phone</h3>
                  <p className="text-gray-300">(480) 555-0123</p>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Address</h3>
                <p className="text-gray-300">
                  <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy<br />
                  Located inside <a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a><br />
                  Gilbert, AZ 85234
                </p>
              </div>
            </div>
          </div>

          {/* Changes to Terms */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Changes to Terms of Service
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300 mb-4">
                We reserve the right, at our sole discretion, to modify or replace these Terms at 
                any time. If a revision is material, we will try to provide at least 30 days notice 
                prior to any new terms taking effect.
              </p>
              <p className="body-medium text-gray-300 mb-4">
                What constitutes a material change will be determined at our sole discretion. 
                By continuing to access or use our Service after those revisions become effective, 
                you agree to be bound by the revised terms.
              </p>
              <p className="body-medium text-gray-300">
                If you do not agree to the new terms, please stop using the Service.
              </p>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
            <div className="flex items-start">
              <Gavel style={{ color: 'var(--brand-primary)' }} size={32} className="mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Legal Notice</h3>
                <p className="body-small text-gray-400 italic">
                  These Terms of Service have been prepared to comply with applicable laws and regulations. 
                  They should be reviewed by qualified legal counsel to ensure compliance with current 
                  federal and state regulations. <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy reserves the right to 
                  update these terms as required by law or changes in our business practices.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};